import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  useGetViewBoardsQuery,
  usePostCreateBoardMutation,
  usePostSavePinMutation,
} from "../store/api.ts";

const formatUpdatedLabel = (dateValue) => {
  if (!dateValue) return "UPDATED RECENTLY";

  const updatedDate = new Date(dateValue);

  if (Number.isNaN(updatedDate.getTime())) {
    return "UPDATED RECENTLY";
  }

  const now = new Date();
  const diffMs = now.getTime() - updatedDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return "UPDATED NOW";
  if (diffMinutes < 60) return `UPDATED ${diffMinutes}M`;
  if (diffHours < 24) return `UPDATED ${diffHours}H`;
  if (diffDays < 7) return `UPDATED ${diffDays}D`;

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) return `UPDATED ${diffWeeks}W`;

  return `UPDATED ${updatedDate
    .toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    })
    .toUpperCase()}`;
};

const normalizeBoardsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.boards)) return data.boards;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const getApiErrorMessage = (error, fallbackMessage) => {
  return (
    error?.data?.detail ||
    error?.data?.message ||
    error?.data?.error ||
    error?.error ||
    fallbackMessage
  );
};

const getProductPayload = (product) => {
  if (!product) return null;

  const productType =
    product?.productType ||
    product?.product_type ||
    (product?.is_marketplace === false ? "product" : "marketplace");

  const slug =
    product?.real_slug ||
    product?.slug ||
    product?.productSlug ||
    product?.product_url ||
    product?.url ||
    "";

  const matchPercentage =
    product?.matchPercentage ??
    product?.match_percentage ??
    product?.product_score ??
    null;

  return {
    slug: String(slug || "").trim(),
    product_type: productType,
    match_percentage:
      matchPercentage !== null &&
      matchPercentage !== undefined &&
      matchPercentage !== ""
        ? Math.round(Number(matchPercentage))
        : null,
  };
};

const BoardThumbnail = ({ board, isSelected }) => {
  const imageOne = board?.image_one;
  const imageTwo = board?.image_two;
  const imageThree = board?.image_three;

  const hasAnyImage = Boolean(imageOne || imageTwo || imageThree);

  if (!hasAnyImage) {
    return (
      <div className="h-[56px] w-[56px] shrink-0 overflow-hidden rounded-[3px] bg-[#343434]">
        <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-[1px] p-[4px]">
          <div className="rounded-[2px] bg-[#A5A5A5]" />
          <div className="row-span-2 rounded-[2px] bg-[#7E7E7E]" />
          <div className="rounded-[2px] bg-[#8D8D8D]" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-[56px] w-[56px] shrink-0 overflow-hidden rounded-[3px] bg-[#343434]">
      <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-[1px]">
        <div className="overflow-hidden bg-[#555]">
          {imageOne ? (
            <img src={imageOne} alt="" className="h-full w-full object-cover" />
          ) : null}
        </div>

        <div className="row-span-2 overflow-hidden bg-[#777]">
          {imageTwo || imageOne ? (
            <img
              src={imageTwo || imageOne}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>

        <div className="overflow-hidden bg-[#666]">
          {imageThree || imageOne ? (
            <img
              src={imageThree || imageOne}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

const SaveToBoardModal = ({
  isOpen,
  product,
  onClose,
  onRequireAuth,
  onSaved,
}) => {
  const [selectedBoardSlug, setSelectedBoardSlug] = useState("");

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newBoardName, setNewBoardName] = useState("");
  const [modalError, setModalError] = useState("");

  const {
    data: boardsData,
    isLoading: isBoardsLoading,
    isFetching: isBoardsFetching,
    isError: isBoardsError,
    refetch: refetchBoards,
  } = useGetViewBoardsQuery(undefined, {
    skip: !isOpen,
  });

  const [savePin, { isLoading: isSavingPin }] = usePostSavePinMutation();
  const [createBoard, { isLoading: isCreatingBoard }] =
    usePostCreateBoardMutation();

  const boards = normalizeBoardsResponse(boardsData);

  const productPayload = useMemo(() => getProductPayload(product), [product]);

  const productName =
    product?.name || product?.product_name || product?.brand || "Product";

  const productSubtitle = [
    product?.brand || product?.garment_type || product?.category,
    product?.price,
  ]
    .filter(Boolean)
    .join(" · ");

  const productImage =
    product?.image || product?.image_url || product?.main_images?.[0] || "";

  const selectedBoard = boards.find(
    (board) => String(board?.slug || "") === String(selectedBoardSlug || ""),
  );

  useEffect(() => {
    if (!isOpen) return;

    setModalError("");
    setIsCreateOpen(false);
    setNewBoardName("");
    setSelectedBoardSlug("");
  }, [isOpen, product]);

  useEffect(() => {
    if (!isOpen) return;
    if (selectedBoardSlug) return;
    if (!boards.length) return;

    setSelectedBoardSlug(boards[0]?.slug || "");
  }, [isOpen, boards, selectedBoardSlug]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSavingPin && !isCreatingBoard) {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSavingPin, isCreatingBoard, onClose]);

  if (!isOpen) return null;

  const isBusy = isSavingPin || isCreatingBoard;

  const handleCreateBoard = async () => {
    const trimmedName = newBoardName.trim();

    if (!trimmedName || isBusy) return;

    const token = localStorage.getItem("authToken");

    if (!token) {
      onRequireAuth?.();
      return;
    }

    try {
      setModalError("");

      const createdBoard = await createBoard({
        boardCreate: {
          board_name: trimmedName,
        },
      }).unwrap();

      await refetchBoards();

      if (createdBoard?.slug) {
        setSelectedBoardSlug(createdBoard.slug);
      }

      setIsCreateOpen(false);
      setNewBoardName("");
      toast.success("Board created.");
    } catch (error) {
      setModalError(getApiErrorMessage(error, "Failed to create board."));
    }
  };

  const handleSaveToBoard = async () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      onRequireAuth?.();
      return;
    }
    if (!selectedBoardSlug) {
      setModalError("Please select a board.");
      return;
    }

    if (!productPayload?.slug) {
      setModalError("Product slug is not available.");
      return;
    }

    try {
      setModalError("");

      const pinPayload = {
        board_slug: selectedBoardSlug,
        slug: productPayload.slug,
        product_type: productPayload.product_type,
      };

      if (
        productPayload.match_percentage !== null &&
        productPayload.match_percentage !== undefined &&
        Number.isFinite(productPayload.match_percentage)
      ) {
        pinPayload.match_percentage = productPayload.match_percentage;
      }

      const response = await savePin({
        pinSave: pinPayload,
      }).unwrap();

      toast.success(response?.detail || "Product saved to board.");
      onSaved?.(response);
      onClose?.();
    } catch (error) {
      setModalError(getApiErrorMessage(error, "Failed to save product."));
      toast.error(getApiErrorMessage(error, "Failed to save product."))
    }
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close save to board modal"
        disabled={isBusy}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/55"
      />

      <div className="relative z-[141] w-full max-w-[520px] rounded-[6px] bg-white px-8 pb-7 pt-8 shadow-[0_22px_80px_rgba(0,0,0,0.28)]">
        <button
          type="button"
          disabled={isBusy}
          onClick={onClose}
          className="absolute right-8 top-8 flex h-[28px] w-[28px] items-center justify-center rounded-[4px] bg-[#F7F7F7] font-inter text-[15px] leading-none text-black transition hover:bg-[#ECECEC] disabled:cursor-not-allowed disabled:opacity-60"
        >
          ×
        </button>

        <p className="font-spaceMono text-[11px] uppercase tracking-[0.18em] text-[#5C5C5C] font-normal">
          Save to board
        </p>

        <div className="mt-5 flex items-center gap-4">
          <div className="h-[52px] w-[52px] shrink-0 overflow-hidden rounded-[6px] bg-[#3B3A3A]">
            {productImage ? (
              <img
                src={productImage}
                alt={productName}
                className="h-full w-full object-cover"
              />
            ) : null}
          </div>

          <div className="min-w-0">
            <h2 className="truncate font-inter text-[16px] md:tecxt-[22px] font-semibold leading-tight text-[#1E1E1E]">
              Save {productName}
            </h2>

            {productSubtitle ? (
              <p className="mt-1 truncate font-spaceMono text-[11px] font-normal uppercase tracking-[0.08em] text-[#5C5C5C]">
                {productSubtitle}
              </p>
            ) : null}
          </div>
        </div>

        <p className="mt-7 font-spaceMono text-[10px] uppercase tracking-[0.18em] text-[#5C5C5C]">
          Your boards
        </p>

        <div className="mt-3 max-h-[310px] space-y-[6px] overflow-y-auto pr-1">
          {isBoardsLoading || isBoardsFetching ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-[58px] animate-pulse rounded-[4px] border border-[#EFEFEF] bg-[#FAFAFA]"
              />
            ))
          ) : isBoardsError ? (
            <div className="rounded-[4px] border border-[#EFEFEF] bg-[#FAFAFA] px-4 py-5 text-center">
              <p className="font-inter text-[13px] text-[#555]">
                Failed to load boards.
              </p>

              <button
                type="button"
                onClick={refetchBoards}
                className="mt-3 h-[32px] rounded-[4px] bg-[#252525] px-4 font-inter text-[12px] text-white"
              >
                Retry
              </button>
            </div>
          ) : boards.length === 0 ? (
            <div className="rounded-[4px] border border-[#EFEFEF] bg-[#FAFAFA] px-4 py-5 text-center">
              <p className="font-inter text-[13px] text-[#555]">
                No boards yet. Create one below.
              </p>
            </div>
          ) : (
            boards.map((board) => {
              const isSelected =
                String(selectedBoardSlug || "") === String(board?.slug || "");
              return (
                <button
                  key={board.id}
                  type="button"
                  disabled={isBusy}
                  onClick={() => setSelectedBoardSlug(board.slug || "")}
                  className={[
                    "flex h-[64px] w-full items-center gap-3 rounded-[4px] border bg-white px-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60",
                    isSelected
                      ? "border-[#8F333B] bg-[#FFF8F5] border-[2px]"
                      : "border-[#EFEFEF] hover:border-[#DADADA]",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full border",
                      isSelected
                        ? "border-[#8F333B] border-[7px]"
                        : "border-[#EDEDED] border-[1.2px]",
                    ].join(" ")}
                  >
                    {isSelected ? (
                      <span className="h-[8px] w-[8px] rounded-full bg-white" />
                    ) : null}
                  </span>

                  <BoardThumbnail board={board} isSelected={isSelected} />

                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-inter text-[12px] md:text-[14px] font-medium text-[#1E1E1E]">
                      {board?.name || "Untitled board"}
                    </span>

                    <span className="mt-1 block truncate font-spaceMono text-[10px] uppercase tracking-[0.08em] text-[#5C5C5C]">
                      {Number(board?.pin_count || 0)} items ·{" "}
                      {formatUpdatedLabel(board?.updated_at)}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="mt-6 rounded-[4px] border border-[#EFEFEF] bg-white">
          {isCreateOpen ? (
            <div className="p-3">
              <input
                value={newBoardName}
                onChange={(event) => setNewBoardName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleCreateBoard();
                  }
                }}
                placeholder="Board name"
                disabled={isBusy}
                autoFocus
                className="h-[38px] w-full rounded-[4px] border border-[#DADADA] px-3 font-inter text-[13px] outline-none focus:border-[#252525] disabled:cursor-not-allowed disabled:bg-[#F7F7F7]"
              />

              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => {
                    setIsCreateOpen(false);
                    setNewBoardName("");
                  }}
                  className="h-[32px] rounded-[4px] border border-[#E2E2E2] px-4 font-inter text-[12px] text-[#222] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={!newBoardName.trim() || isBusy}
                  onClick={handleCreateBoard}
                  className="h-[32px] rounded-[4px] bg-[#252525] px-4 font-inter text-[12px] text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isCreatingBoard ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={isBusy}
              onClick={() => setIsCreateOpen(true)}
              className="flex h-[52px] w-full items-center gap-4 px-3 text-left transition hover:bg-[#FAFAFA] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[8px] bg-[#1F1F1F] font-medium font-inter text-[20px] leading-none text-white">
                +
              </span>

              <span className="min-w-0 flex-1">
                <span className="block font-inter text-[14px] font-medium text-[#1E1E1E]">
                  Create new board
                </span>
                <span className="block font-inter text-[11px] text-[#5C5C5C] font-medium">
                  Start a new collection
                </span>
              </span>

              <span className="font-inter text-[18px] text-[#777]">›</span>
            </button>
          )}
        </div>

        {modalError ? (
          <p className="mt-4 font-inter text-[12px] text-[#D43F3F]">
            {modalError}
          </p>
        ) : null}

        <button
          type="button"
          disabled={!selectedBoardSlug || isBusy || isBoardsLoading}
          onClick={handleSaveToBoard}
          className="mt-5 h-[46px] md:h-[52px] w-full rounded-[5px] bg-[#8F333B] font-inter text-[13px] font-medium text-white transition hover:opacity-[95%] disabled:cursor-not-allowed disabled:opacity-[80%]"
        >
          {isSavingPin
            ? "Saving..."
            : selectedBoard
              ? `Save to ${selectedBoard?.name || "board"}`
              : "Save to board"}
        </button>
      </div>
    </div>
  );
};

export default SaveToBoardModal;
